import React from "react";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { useConstant } from "@hooks/useConstant";
import classNames from "classnames";
import { differenceInDays } from "date-fns";
import { currencyFormatterNoFractions } from "@common/helpers/currency";
import { pluralize } from "@common/helpers/string";
import {
    flatRatePlanNames,
    ENTITY_TRANSACTIONS_THRESHOLD,
    FLAT_RATE_PLANS,
} from "@common/flatRateBilling";
import { useCanManageWorkspaceBilling } from "@hooks/permissions/useCanManageWorkspaceBilling";
import { SupportChat } from "@components/navigation/SupportChat";
import { useBillingStatus } from "@hooks/useBillingStatus";
import { Tag } from "@design-system/Tag/Tag";
import { useSettings } from "../../settings/SettingsProvider";
import styles from "./BillingState.module.scss";

interface BillingStateProps {
    /** The help button before the plan / trial chip. */
    helpMenu?: React.ReactNode;
}

export const BillingState: React.FC<BillingStateProps> = ({
    helpMenu = <SupportChat />,
}) => {
    const {
        isTrialing,
        isSubscribed,
        totalUsage,
        plans,
        periodEnd,
        currentPlan,
        isFetched,
        bookedEnterpriseCall,
        isV2Plan,
        isV3Plan,
        transactionCount,
        nextPlan,
    } = useBillingStatus();
    const canManageBilling = useCanManageWorkspaceBilling();
    const { open } = useSettings();
    const now = useConstant(() => new Date());

    if (!isFetched) {
        return null;
    }

    const freePlan = plans[0];

    let state: React.ReactNode;

    if (!canManageBilling) {
        state = null;
    } else if (!isSubscribed) {
        const renderFreeState = (params: {
            isOver: boolean;
            label: React.ReactNode;
            tooltip: React.ReactNode;
        }) => (
            <Tooltip content={params.tooltip}>
                <button
                    className={classNames(styles.billingState, {
                        [styles.warning]: params.isOver,
                    })}
                    onClick={() => open(["plans"])}
                >
                    {params.label}
                    <Tag size="sm" color="accent">
                        Upgrade
                    </Tag>
                </button>
            </Tooltip>
        );

        if (isV2Plan || isV3Plan) {
            const clampedCount = Math.min(
                transactionCount,
                ENTITY_TRANSACTIONS_THRESHOLD,
            );
            const isOver = transactionCount >= ENTITY_TRANSACTIONS_THRESHOLD;

            state = renderFreeState({
                isOver,
                label: (
                    <>
                        {transactionCount} / {ENTITY_TRANSACTIONS_THRESHOLD}
                    </>
                ),
                tooltip: isOver ? (
                    <>
                        You've hit the {ENTITY_TRANSACTIONS_THRESHOLD}{" "}
                        transaction limit on the free plan. <br />
                        Upgrade now to continue using Kick.
                    </>
                ) : (
                    <>
                        {clampedCount} of {ENTITY_TRANSACTIONS_THRESHOLD}{" "}
                        {pluralize("transaction", clampedCount)} used on the
                        free plan.
                        <br />
                        Upgrade and put the books on autopilot.
                    </>
                ),
            });
        } else {
            const maxUsage = freePlan.usageLimit ?? 0;
            const usedPercentage = Math.min(
                Math.ceil((totalUsage / maxUsage) * 100),
                100,
            );
            const isOver = totalUsage >= maxUsage;

            state = renderFreeState({
                isOver,
                label: <>{usedPercentage}% used</>,
                tooltip: isOver ? (
                    <>
                        You've hit the{" "}
                        {currencyFormatterNoFractions.format(maxUsage)} free
                        business expense limit. <br />
                        Upgrade now to continue using Kick.
                    </>
                ) : (
                    <>
                        You used {usedPercentage}% of your business expenses.
                        <br />
                        Upgrade and put the books on autopilot with less risk
                        and more savings.
                    </>
                ),
            });
        }
    } else if (isTrialing) {
        if (!bookedEnterpriseCall) {
            const daysLeft = differenceInDays(periodEnd!, now);

            state = (
                <Tooltip
                    title={
                        nextPlan === FLAT_RATE_PLANS.FREE ? (
                            <>
                                Your {flatRatePlanNames[currentPlan]} trial ends
                                in {daysLeft} {pluralize("day", daysLeft)}
                            </>
                        ) : (
                            <>
                                Your {flatRatePlanNames[currentPlan]} plan will
                                start in {daysLeft} {pluralize("day", daysLeft)}
                            </>
                        )
                    }
                    content={
                        nextPlan === FLAT_RATE_PLANS.FREE ? (
                            "You'll move to the free plan."
                        ) : (
                            <>
                                We'll automatically bill your credit card.
                                <br />
                                Just relax and kick back.
                            </>
                        )
                    }
                >
                    <button
                        className={styles.billingState}
                        onClick={() => open(["billing"])}
                    >
                        {daysLeft} {pluralize("day", daysLeft)} left
                        <Tag size="sm" color="accent" outline>
                            {flatRatePlanNames[currentPlan]} Trial
                        </Tag>
                    </button>
                </Tooltip>
            );
        }
    }

    return (
        <section className={styles.billingStateContainer}>
            {helpMenu}

            {state}
        </section>
    );
};
